import { Button } from "@/components/ui/button";
import { CheckCircle2, TruckIcon, Users, Shield, Clock } from "lucide-react";

export function HeroSection() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section 
      id="hero" 
      className="relative flex items-center max-md:items-start pt-20 pb-16 md:pb-20"
      style={{ minHeight: '100dvh' }}
    >
      {/* Background Image with Light Overlay - Using picture element for responsive loading */}
      {/* Fixed dimensions container to prevent CLS */}
      <div 
        className="absolute inset-0 z-0"
        style={{ 
          backgroundColor: '#1a2332',
          contain: 'layout paint'
        }}
      >
        <picture>
          <source
            media="(max-width: 480px)"
            srcSet="/hero/hero-sm.webp"
          />
          <source
            media="(max-width: 960px)"
            srcSet="/hero/hero-md.webp"
          />
          <source
            media="(max-width: 1280px)"
            srcSet="/hero/hero-lg.webp"
          />
          <img
            src="/hero/hero-xl.webp"
            alt="Professional flatbed trucking - Ben Boaz Transport LLC fleet"
            className="w-full h-full object-cover"
            loading="eager"
            decoding="sync"
            width={1920}
            height={1080}
            style={{ 
              position: 'absolute',
              inset: 0,
              objectFit: 'cover'
            }}
          />
        </picture>
        <div 
          className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/35 to-black/25" 
          style={{ contain: 'layout paint' }}
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-6 relative z-10">
        <div className="max-w-4xl">
          <div className="mb-4 md:mb-6">
            <span className="inline-flex items-center gap-2 bg-accent/90 backdrop-blur-sm text-accent-foreground px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-bold text-xs md:text-sm uppercase tracking-wide">
              <CheckCircle2 className="h-3 w-3 md:h-4 md:w-4" />
              Now Hiring Professional Drivers
            </span>
          </div>

          <h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
            data-testid="text-hero-title"
          >
            Professional Truck Driving Jobs - Texas Regional CDL Careers
          </h1>

          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/95 mb-6 md:mb-8 leading-relaxed max-w-3xl">
            Join <span className="font-bold">Ben Boaz Transport LLC</span> - Texas' premier flatbed carrier. 
            Drive old school (ELD Exempt) 48' & 53' equipment hauling steel, lumber, and machinery. 
            Competitive pay, home weekly, dedicated routes.
          </p>

          {/* Quick Benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8 md:mb-10">
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 md:p-4 border border-white/20">
              <div className="flex items-center gap-2 md:gap-3 text-white">
                <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <TruckIcon className="h-4 w-4 md:h-5 md:w-5 text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm md:text-base lg:text-lg">Old School Fleet</div>
                  <div className="text-xs md:text-sm text-white/80">1999 & Older (ELD Exempt)</div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 md:p-4 border border-white/20">
              <div className="flex items-center gap-2 md:gap-3 text-white">
                <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-4 w-4 md:h-5 md:w-5 text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm md:text-base lg:text-lg">Home Weekly</div>
                  <div className="text-xs md:text-sm text-white/80">Regional Routes</div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 md:p-4 border border-white/20">
              <div className="flex items-center gap-2 md:gap-3 text-white">
                <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-4 w-4 md:h-5 md:w-5 text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm md:text-base lg:text-lg">Dedicated Routes</div>
                  <div className="text-xs md:text-sm text-white/80">Consistent Lanes</div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-lg p-3 md:p-4 border border-white/20">
              <div className="flex items-center gap-2 md:gap-3 text-white">
                <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Users className="h-4 w-4 md:h-5 md:w-5 text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm md:text-base lg:text-lg">Great Team</div>
                  <div className="text-xs md:text-sm text-white/80">Family Atmosphere</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
            <Button
              size="lg"
              className="bg-accent hover:bg-accent text-accent-foreground font-bold text-base md:text-lg shadow-xl"
              onClick={() => window.location.href = "/careers#application"}
              data-testid="button-hero-apply"
            >
              APPLY FOR DRIVER POSITION
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white/90 backdrop-blur-sm hover:bg-white border-2 border-white text-foreground font-bold text-base md:text-lg"
              onClick={() => window.location.href = "/contact"}
              data-testid="button-hero-freight-quote"
            >
              REQUEST A FREIGHT QUOTE
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 md:mt-12 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4 sm:gap-6 text-white/90">
            <div>
              <div className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                15+
              </div>
              <div className="text-xs md:text-sm">Years in Business</div>
            </div>
            <div className="hidden sm:block h-8 md:h-12 w-px bg-white/30" />
            <div>
              <div className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                100%
              </div>
              <div className="text-xs md:text-sm">On-Time Delivery</div>
            </div>
            <div className="hidden sm:block h-8 md:h-12 w-px bg-white/30" />
            <div>
              <div className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                A+
              </div>
              <div className="text-xs md:text-sm">Safety Rating</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <button
          onClick={() => scrollToSection("careers")}
          className="flex flex-col items-center gap-2 text-white/80 hover:text-white transition-colors group"
          data-testid="button-scroll-indicator"
        >
          <span className="text-sm font-medium">Explore Opportunities</span>
          <div className="animate-bounce">
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </button>
      </div>
    </section>
  );
}
